/**
 * Maps a pathname to a stable page_name and path_pattern for analytics.
 * Order matters — more specific patterns must come before broader ones.
 */

interface PageInfo {
  page_name: string
  path_pattern: string
}

const PAGE_PATTERNS: Array<{ re: RegExp; page_name: string; path_pattern: string }> = [
  // Onboarding
  { re: /^\/onboarding\/install-script/, page_name: 'onboarding_install_script', path_pattern: '/onboarding/install-script' },
  { re: /^\/onboarding\/add-site/, page_name: 'onboarding_add_site', path_pattern: '/onboarding/add-site' },
  { re: /^\/onboarding/, page_name: 'onboarding_add_site', path_pattern: '/onboarding' },

  // Auth
  { re: /^\/auth/, page_name: 'auth', path_pattern: '/auth' },
  { re: /^\/verify/, page_name: 'verify', path_pattern: '/verify' },
  { re: /^\/checkout-success/, page_name: 'checkout_success', path_pattern: '/checkout-success' },
  { re: /^\/accept-invite/, page_name: 'accept_invite', path_pattern: '/accept-invite/:token' },

  // Hub / top-level
  { re: /^\/dashboard$/, page_name: 'hub', path_pattern: '/dashboard' },
  { re: /^\/workspaces$/, page_name: 'workspaces', path_pattern: '/workspaces' },
  { re: /^\/workspace\/new/, page_name: 'workspace_new', path_pattern: '/workspace/new' },
  { re: /^\/add-website/, page_name: 'add_website', path_pattern: '/add-website' },

  // Settings
  { re: /^\/user-settings/, page_name: 'user_settings', path_pattern: '/user-settings' },

  // Contextual pages (under /p/:id or /workspace/:id) — specific first
  { re: /\/split-testing\/new/, page_name: 'experiment_create', path_pattern: '/:context/split-testing/new' },
  { re: /\/split-tests\/[^/]+\/health/, page_name: 'experiment_health', path_pattern: '/:context/split-tests/:id/health' },
  { re: /\/split-tests\//, page_name: 'experiment_detail', path_pattern: '/:context/split-tests/:id' },
  { re: /\/split-testing/, page_name: 'split_testing', path_pattern: '/:context/split-testing' },
  { re: /\/experiments\/editor/, page_name: 'component_editor', path_pattern: '/:context/experiments/editor/:id' },
  { re: /\/funnels\/studio/, page_name: 'funnel_studio', path_pattern: '/:context/funnels/studio' },
  { re: /\/funnel-analyzer/, page_name: 'funnel_studio', path_pattern: '/:context/funnel-analyzer' },
  { re: /\/funnels\/[^/]+/, page_name: 'funnel_detail', path_pattern: '/:context/funnels/:id' },
  { re: /\/funnels/, page_name: 'funnels', path_pattern: '/:context/funnels' },
  { re: /\/heatmaps/, page_name: 'heatmaps', path_pattern: '/:context/heatmaps' },
  { re: /\/attribution/, page_name: 'attribution', path_pattern: '/:context/attribution' },
  { re: /\/connectors\/[^/]+\/settings/, page_name: 'connector_settings', path_pattern: '/:context/connectors/:type/settings' },
  { re: /\/connectors\/[^/]+/, page_name: 'connector_config', path_pattern: '/:context/connectors/:type' },
  { re: /\/connectors/, page_name: 'connectors', path_pattern: '/:context/connectors' },
  { re: /\/traffic/, page_name: 'traffic', path_pattern: '/:context/traffic' },
  { re: /\/pages/, page_name: 'pages', path_pattern: '/:context/pages' },
  { re: /\/devices/, page_name: 'devices', path_pattern: '/:context/devices' },
  { re: /\/locations/, page_name: 'locations', path_pattern: '/:context/locations' },
  { re: /\/channels/, page_name: 'channels', path_pattern: '/:context/channels' },
  { re: /\/llm-referrals/, page_name: 'llm_referrals', path_pattern: '/:context/llm-referrals' },
  { re: /\/clicks/, page_name: 'clicks', path_pattern: '/:context/clicks' },
  { re: /\/forms/, page_name: 'forms', path_pattern: '/:context/forms' },
  { re: /\/bots/, page_name: 'bots', path_pattern: '/:context/bots' },
  { re: /\/export/, page_name: 'export', path_pattern: '/:context/export' },
  { re: /\/api$/, page_name: 'api_access', path_pattern: '/:context/api' },
  { re: /\/test-planner/, page_name: 'test_planner', path_pattern: '/:context/test-planner' },
  { re: /\/resources/, page_name: 'resources', path_pattern: '/:context/resources' },
  { re: /\/settings/, page_name: 'settings', path_pattern: '/:context/settings' },
  { re: /\/tools\/utm-generator/, page_name: 'tool_utm_generator', path_pattern: '/:context/tools/utm-generator' },
  { re: /\/tools\/significance-calculator/, page_name: 'tool_significance_calculator', path_pattern: '/:context/tools/significance-calculator' },
  { re: /\/tools\/conversion-rate-calculator/, page_name: 'tool_conversion_rate_calculator', path_pattern: '/:context/tools/conversion-rate-calculator' },
  { re: /\/tools/, page_name: 'tools', path_pattern: '/:context/tools' },

  // Share routes
  { re: /^\/s\//, page_name: 'share_view', path_pattern: '/s/:id/:password' },
]

export function getPageInfo(pathname: string): PageInfo {
  for (const entry of PAGE_PATTERNS) {
    if (entry.re.test(pathname)) {
      return { page_name: entry.page_name, path_pattern: entry.path_pattern }
    }
  }
  return { page_name: 'unknown', path_pattern: pathname }
}
